// ── Backlog management view — unscheduled work only ──
//
// Renders every `priority === 0` task as a flat list — no sprint sections/lanes of its own
// (Board / Planning / the task detail panel own those views). Sprint assignment from here is
// a one-way ACTION: the per-row `.backlog-row-sprint` select and the drop rail move a task OUT
// of this list via moveTasksToSprint() — see that function for why the PATCHes are serialized
// rather than Promise.all'd (C1464/TPT2 cascade races). Ticked rows get the shared bulk-action
// toolbar (components/bulk-actions.js, TPT9098), which changes the whole selection with one
// PATCH /tasks/bulk or POST /tasks/bulk-delete through the same write queue.
//
// (TPT630) Data sources — never the project's whole task list: the rows are
// GET /tasks?priority=0&fields=board, and the sprint numbers (selects, rail targets) and rail
// counts come from GET /tasks/facets `priorities` / `priority_counts` (see loadBacklogData()).

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, renderAvatarHtml, TYPES, TYPE_COLORS, TYPE_LABELS, getTypeIcon, sprintOptionsHtml, nextSprintNumber } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { renderMemberCombo, createMemberCombo } from '../components/member-combo.js';
import { reserveTaskKey, releaseTaskKey } from '../lib/task-key-reservation.js';
import { loadStatuses, roleStatus } from '../lib/statuses.js';
import { sprintsEnabled } from '../lib/sprints-enabled.js';
import { groupLabelSingular, groupLabelPlural } from '../lib/task-group-label.js';
import { loadProject } from '../lib/project.js';
import { can, applyWriteGate } from '../lib/permissions.js';
import { sprintLabel } from '../components/board-filters.js';
import { railTargets } from '../lib/backlog-rail.js';
import { findInsertBefore, dropIndex } from '../lib/drag-order.js';
import { getNavigationId } from '../router.js';
import { createBulkSelection, renderBulkToolbar, BULK_CASCADE_KEYS } from '../components/bulk-actions.js';

let state = {
  projectId: null,
  // Backlog rows (priority 0) — plus any row optimistically moved to a sprint since the last
  // fetch, which keeps its new priority here until the next refetch drops it.
  tasks: [],
  // GET /tasks/facets `priorities` as `{ priority }` rows — the project-wide sprint numbers in
  // use, standing in for the full list this page no longer holds (board.js facetSprintRows()).
  sprintRows: [],
  // GET /tasks/facets `priority_counts` — { <sprint number>: tasks }, the rail's counts, bumped
  // locally by every optimistic move (bumpPriorityCount()).
  priorityCounts: {},
  members: [],
  statuses: [],
  sprints: [],
  project: null,
  // Ticked rows (TPT9098) — only ever keys of rendered rows: renderBacklog() drops the rest.
  selection: createBulkSelection(),
  drag: null,
  // Owner tokens of the drawer and modal this page opened (TPT647), passed to their closers on
  // teardown so a late teardown leaves another page's drawer/modal open.
  detailToken: null,
  modalToken: null,
};

function resetState() {
  state = {
    projectId: null, tasks: [], sprintRows: [], priorityCounts: {}, members: [], statuses: [],
    sprints: [], project: null,
    selection: createBulkSelection(),
    drag: null,
    detailToken: null,
    modalToken: null,
  };
}

// ── Sprint rail collapse (TPT43) ──
// Module-scoped, NOT localStorage and NOT part of `state` — same idiom as sprint-board.js's
// collapsedLanes/collapsedLanesProjectId: resetState() runs on every backlogPage() mount
// (including a re-navigation to the SAME project), and the user's collapse choice must
// survive that. Reset only on a genuine project switch (see backlogPage() below).
let railCollapsed = false;
let railCollapsedProjectId = null;

// ── Assignee combo (TPT44) ──
// At most ONE row combo is live at a time — module-scoped (not `state`) because it owns a
// createMemberCombo instance whose window-resize listener leaks unless destroy() runs before
// the DOM under it is discarded. renderBacklog() closes it unconditionally on every render
// (a drag reorder / sprint move / refetch mid-edit must not orphan the instance).
let rowCombo = null; // { taskKey, wrap, instance }

// ── Bulk-action toolbar (TPT9098) ──
// Module-scoped like rowCombo: the instance owns member-combo and tag-typeahead widgets whose
// window listeners leak unless destroy() runs. It lives in #backlog-bulk-host, outside
// #backlog-container, so renderBacklog() never rebuilds it — a focused field survives every
// repaint. Created once per mount (task:write only), destroyed by the teardown or the next mount.
let bulkToolbar = null;

// ── Inline-create key reservation (C980) ──
// Lazily booked on first interaction with the inline-create row (not on every render,
// so merely viewing the backlog doesn't mint keys) and released whenever the row is
// abandoned: blurred without submitting, rebuilt by renderBacklog(), or the page
// unmounts. Module-level (not part of `state`) since it tracks a reservation that
// outlives any single renderBacklog() rebuild of the row's DOM.
// (C1483) No per-type prefix map — every CODING key (task/story/bug alike) reserves
// this project's own task_prefix, `type` rides along as its own field. epic still never
// reserves here — the server auto-generates epic keys (generateEpicKey), handled by the
// `type === 'epic'` branch below.
let inlineReservedTask = null;
let inlineReservedProjectId = null;
let inlineReserving = false;
let inlineReserveSeq = 0;

function releaseInlineReservation() {
  inlineReserveSeq++; // invalidate any in-flight reservation resolution
  if (inlineReservedTask && inlineReservedProjectId != null) {
    releaseTaskKey(inlineReservedProjectId, inlineReservedTask.task_key);
  }
  inlineReservedTask = null;
  inlineReserving = false;
}

// Ensures the reservation matches the currently-selected type: epic needs none (the
// server auto-generates that key at submit time); task/story/bug need a real reserved
// key with the matching prefix. Safe to call on every focus/type-change — no-ops if
// the existing reservation already matches.
async function syncInlineReservation() {
  const typeSelect = document.getElementById('backlog-new-type');
  const keyInput = document.getElementById('backlog-new-key');
  const type = typeSelect ? typeSelect.value : 'task';

  if (type === 'epic') {
    if (inlineReservedTask) releaseInlineReservation();
    if (keyInput) { keyInput.value = ''; keyInput.placeholder = '(auto)'; }
    return;
  }

  if (inlineReservedTask && inlineReservedTask.type === type) return; // already correct
  if (inlineReservedTask) releaseInlineReservation();
  if (inlineReserving) return;

  inlineReserving = true;
  const mySeq = ++inlineReserveSeq;
  const projectId = state.projectId;
  try {
    const task = await reserveTaskKey(projectId, { category: 'CODING', type });
    if (mySeq !== inlineReserveSeq || projectId !== state.projectId) {
      // Superseded by a type change, blur-release, re-render, or page navigation.
      releaseTaskKey(projectId, task.task_key);
      return;
    }
    inlineReservedTask = task;
    inlineReservedProjectId = projectId;
    if (keyInput) { keyInput.value = task.task_key; keyInput.placeholder = 'Key'; }
  } catch (err) {
    showToast(err.message || 'Failed to reserve task key', 'error');
  } finally {
    inlineReserving = false;
  }
}

// ── Sprint controls ──
//
// The per-row select is an ACTION, not a bound state field: every row here is priority 0, so
// nothing is pre-selected and there is no "Backlog" option (moving a priority-0 row to 0 is a
// no-op). The bulk toolbar's Sprint control drops its Backlog option for the same reason
// (`includeBacklog: false` in backlogPage()).
function sprintSingular() {
  return groupLabelSingular(state.project && state.project.task_group_label); // C1234/C1395
}

// Rows whose `priority` names every sprint number in use: the facet rows (project-wide) plus
// this page's own rows, so a number a row was just moved to shows before the next refetch.
function sprintSourceRows() {
  return [...state.sprintRows, ...state.tasks];
}

// Keeps the rail counts in step with an optimistic or reconciled priority change; `from` is
// null for a row that did not exist before (a create).
function bumpPriorityCount(from, to) {
  if (from === to) return;
  const counts = state.priorityCounts;
  if (from != null && counts[from]) counts[from] -= 1;
  if (to != null) counts[to] = (counts[to] || 0) + 1;
}

function getSprintOptions(placeholder) {
  const label = sprintSingular();
  const rows = sprintSourceRows();
  return `<option value="" disabled selected>${escapeHtml(placeholder)}</option>`
    + sprintOptionsHtml(state.sprints, rows, null, label)
    + `<option value="${nextSprintNumber(state.sprints, rows)}">+ New ${escapeHtml(label)}</option>`;
}

// Every control on this page mutates tasks, so one task:write check gates them all.
// False until the project (and its capabilities) has loaded.
function canWrite() {
  return can(state.project, getUser(), 'task:write');
}

// ── Rendering ──

// escapeHtml() escapes & < > but not '"' — needed for values written into attributes.
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

// (TPT9142) A row's trailing controls: one grid slot each, always in this order — tags, story
// points, sprint select, assignee. Every slot renders even when it holds nothing, so
// `.backlog-row-meta` (styles.css § Backlog View) puts each control at the same x offset on
// every row. `sprintSelectHtml` is null for the whole page when there is no sprint select (no
// task:write, or sprints off — C1334), which drops that slot from every row alike.
export function backlogRowMetaHtml({ tags, storyPoints, sprintSelectHtml, assigneeHtml }) {
  const all = tags || [];
  let html = `<div class="backlog-row-meta${sprintSelectHtml ? '' : ' backlog-row-meta--no-sprint'}">`;
  // One line of chips, clipped where the slot ends, and a tooltip naming every tag.
  html += `<div class="backlog-row-meta__tags"${all.length ? ` title="${escapeAttr(all.join(', '))}"` : ''}>`;
  html += all.slice(0, 3).map(t => `<span class="tag">${escapeHtml(t)}</span>`).join('');
  if (all.length > 3) html += `<span class="backlog-row-meta__more text-faint">+${all.length - 3}</span>`;
  html += `</div>`;
  html += `<span class="backlog-row-meta__points">`;
  if (storyPoints != null) html += `<span class="backlog-sp-pill" title="Story points">${escapeHtml(String(storyPoints))}</span>`;
  html += `</span>`;
  if (sprintSelectHtml) html += sprintSelectHtml;
  html += assigneeHtml;
  html += `</div>`;
  return html;
}

function renderBacklogRow(task, sprintOptions) {
  const writable = canWrite();
  const checked = state.selection.has(task.task_key) ? 'checked' : '';
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  const assigneeUser = task.assignee
    ? state.members.find(m => (m.user_id || m.id) === task.assignee)
    : null;

  let html = `<div class="backlog-row" data-task-key="${escapeHtml(task.task_key)}">`;
  if (writable) html += `<input type="checkbox" class="backlog-checkbox" data-task-key="${escapeHtml(task.task_key)}" ${checked}>`;
  html += getTypeIcon(task.type);
  html += `<a class="badge ${catClass} task-key-link" style="flex-shrink:0;" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`;
  html += `<span class="backlog-row-title">${escapeHtml(task.title)}</span>`;
  let sprintSelectHtml = null;
  if (sprintOptions) {
    const label = sprintSingular();
    sprintSelectHtml = `<select class="backlog-inline-type backlog-row-sprint" data-task-key="${escapeHtml(task.task_key)}"`
      + ` title="Move to ${escapeHtml(label)}" aria-label="Move ${escapeHtml(task.task_key)} to a ${escapeHtml(label)}">`
      + sprintOptions
      + `</select>`;
  }
  // Read-only rows keep the avatar but drop `.card-avatar-wrap`, so a click on it falls
  // through to the row and opens the task instead of the assignee combo.
  const assigneeHtml = `<div class="${writable ? 'card-avatar-wrap ' : ''}backlog-row-assignee" data-task-key="${escapeHtml(task.task_key)}" data-action="assign">`
    + `<span class="backlog-row-avatar">${renderAvatarHtml(assigneeUser, 24)}</span>`
    + `</div>`;
  html += backlogRowMetaHtml({ tags: task.tags, storyPoints: task.story_points, sprintSelectHtml, assigneeHtml });
  html += `</div>`;
  return html;
}

function renderBacklog() {
  const container = document.getElementById('backlog-container');
  if (!container) return;

  // The inline-create row (if any) is about to be torn down and rebuilt below —
  // release any pending reservation now rather than leaking it (C980).
  releaseInlineReservation();
  // A drag in flight is bound to DOM nodes this innerHTML wipe is about to detach — kill it
  // (not cleanupDrag(): the document listeners must stay bound). state.drag going null makes
  // the pending onMouseMove/onMouseUp no-op on their `if (!d) return;` guards, so the drag
  // dies quietly instead of writing an order derived from a detached placeholder.
  abortDrag();
  // Any open row assignee combo is bound to a wrap this innerHTML wipe is about to detach —
  // destroy it now (TPT44), never leave it dangling (its window-resize listener leaks).
  closeRowCombo();

  const backlogTasks = state.tasks.filter(t => t.priority === 0).sort((a, b) => b.display_order - a.display_order);
  // The selection never holds a row this paint does not render — one moved to a sprint, or
  // gone after a refetch — so the toolbar's count is always the visible ticks (TPT9098).
  const rendered = new Set(backlogTasks.map(t => t.task_key));
  state.selection.retain(key => rendered.has(key));
  const writable = canWrite();
  // Sprint selects and the drop rail are move actions — nothing to show without task:write.
  const showSprint = writable && sprintsEnabled(state.project); // C1334
  const singular = sprintSingular();
  const plural = groupLabelPlural(state.project && state.project.task_group_label);
  // Built once per render, shared by every row — sprintOptionsHtml()'s output is identical
  // for all of them (no per-row `selected` state, these are action selects).
  const rowSprintOptions = showSprint ? getSprintOptions(`${singular}...`) : null;

  let html = '';

  // ── Two-column layout: unscheduled list + sprint drop rail (TPT43) ──
  html += `<div class="backlog-layout${showSprint ? ' has-rail' : ''}${writable ? '' : ' board-readonly'}">`;

  html += `<div class="backlog-main">`;

  // Product Backlog section — the select-all box lines up with the rows' checkboxes below it.
  html += `<div class="backlog-section-header">`;
  if (writable && backlogTasks.length > 0) {
    html += `<input type="checkbox" class="backlog-select-all" aria-label="Select all backlog items" title="Select all">`;
  }
  html += `<h3>Product Backlog</h3>`;
  html += `<span class="backlog-section-count">${backlogTasks.length} item${backlogTasks.length !== 1 ? 's' : ''}</span>`;
  html += `</div>`;

  html += `<div class="backlog-list" data-priority="0">`;
  if (backlogTasks.length === 0) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">No backlog items.${writable ? ' Add one below.' : ''}</div>`;
  }
  html += backlogTasks.map(t => renderBacklogRow(t, rowSprintOptions)).join('');
  html += `</div>`;

  // Inline create — focusing it reserves a task key server-side, so it is not rendered
  // at all without task:write.
  if (writable) {
    html += `<div class="backlog-inline-create" id="backlog-inline-create">`;
    html += `<span class="backlog-inline-plus">+</span>`;
    html += `<input type="text" class="backlog-inline-key" id="backlog-new-key" placeholder="Key" readonly>`;
    html += `<input type="text" class="backlog-inline-input" id="backlog-new-title" placeholder="Add item...">`;
    html += `<select class="backlog-inline-type" id="backlog-new-type">`;
    TYPES.forEach(t => { html += `<option value="${t}">${TYPE_LABELS[t]}</option>`; });
    html += `</select>`;
    html += `</div>`;
  }

  html += `</div>`; // .backlog-main

  // ── Sprint drop rail (TPT43) — MUST come after .backlog-main in source order: at the
  // ≤768px breakpoint the layout stacks to one column and the rail must land below the
  // list, not above it (see styles.css § .backlog-layout media query). ──
  if (showSprint) {
    html += `<aside class="backlog-sprint-rail${railCollapsed ? ' backlog-sprint-rail-collapsed' : ''}"`
          + ` id="backlog-sprint-rail" aria-label="Drop targets: ${escapeHtml(plural)}">`;
    html +=   `<button type="button" class="backlog-rail-toggle" id="backlog-rail-toggle"`
          +   ` aria-expanded="${!railCollapsed}" title="${railCollapsed ? 'Expand' : 'Collapse'}">`
          +     `<span class="backlog-rail-chevron">&#9662;</span>`
          +     `<span class="backlog-rail-toggle-label">${escapeHtml(plural)}</span>`
          +   `</button>`;
    html +=   `<div class="backlog-rail-targets">`;
    for (const t of railTargets(state.sprints, sprintSourceRows())) {
      const label = t.isNew ? `+ New ${singular}` : sprintLabel(state.sprints, t.number, singular);
      const cls = 'backlog-sprint-rail-target'
        + (t.closed ? ' backlog-sprint-rail-target-closed' : '')
        + (t.isNew  ? ' backlog-sprint-rail-target-new'    : '');
      const count = state.priorityCounts[t.number] || 0;
      html += `<div class="${cls}" data-priority="${t.number}" title="Drop here to move to ${escapeHtml(label)}">`
            +   `<span class="backlog-rail-target-title">${escapeHtml(label)}</span>`
            +   (t.isNew ? '' : `<span class="backlog-rail-target-count">${count}</span>`)
            + `</div>`;
    }
    html +=   `</div>`;
    html += `</aside>`;
  }

  html += `</div>`; // .backlog-layout

  container.innerHTML = html;
  syncSelectionUi(); // the select-all box's indeterminate state has no HTML attribute
  if (bulkToolbar) bulkToolbar.refresh(); // sprint numbers in use may have changed
}

// ── Bulk selection + toolbar (TPT9098) ──

// Ticks follow the selection in place — never a renderBacklog(), which would drop an
// inline-create reservation or an open row combo just because a box was ticked.
function syncSelectionUi() {
  const container = document.getElementById('backlog-container');
  if (!container) return;
  let rows = 0;
  let ticked = 0;
  container.querySelectorAll('.backlog-checkbox').forEach((cb) => {
    cb.checked = state.selection.has(cb.dataset.taskKey);
    rows += 1;
    if (cb.checked) ticked += 1;
  });
  const all = container.querySelector('.backlog-select-all');
  if (all) {
    all.checked = rows > 0 && ticked === rows;
    all.indeterminate = ticked > 0 && ticked < rows;
  }
}

// The toolbar's onApplied: the response's rows are re-read after the server's cascades, so
// they win over this page's copies. Tasks other than the selected ones moved too when a
// cascade key is present, and a delete removes whole subtrees (often sitting in sprints) —
// both refetch, which also reloads the rail counts. Returned so the write queue waits for it.
function applyBulkResult(result) {
  if (result.action === 'delete') return refetchTasks();
  for (const row of result.tasks || []) {
    const idx = state.tasks.findIndex(t => t.task_key === row.task_key);
    if (idx < 0) continue;
    if (typeof row.priority === 'number') bumpPriorityCount(state.tasks[idx].priority, row.priority);
    state.tasks[idx] = { ...state.tasks[idx], ...row };
  }
  if (BULK_CASCADE_KEYS.some(key => Array.isArray(result[key]) && result[key].length > 0)) return refetchTasks();
  renderBacklog();
  return null;
}

// ── Move to sprint ──
//
// PATCHes are SERIALIZED on purpose — never Promise.all. A `priority` write fires the C1464
// objective-child cascade and/or the TPT2 parent cascade, both of which rewrite OTHER tasks'
// priorities (and INSERT IGNORE their sprints rows) inside the same request; the server
// re-SELECTs this task afterwards, so the returned priority can be HIGHER than the one sent.
// Every row on this page is created is_objective (C1341), so that is the normal path here, not
// the exotic one — concurrent PATCHes across one objective tree would each read the others'
// pre-cascade state. The module-level chain below extends that serialization across
// overlapping user actions (e.g. a row select clicked while a bulk batch is still in flight).
//
// (TPT43) The drag-and-drop within-list reorder writes display_order-only PATCHes through
// this SAME chain via enqueueWrite()/persistBacklogOrder() below — a reorder fires no
// cascade on its own, but must not interleave with an in-flight sprint-move batch whose
// tail refetchTasks() would clobber its optimistic ordering.
//
// (TPT9098) The bulk toolbar's requests — one PATCH /tasks/bulk or POST /tasks/bulk-delete
// per action — queue here too (backlogPage() hands it enqueueWrite()), and the chain waits
// for applyBulkResult()'s refetch, so a row move never lands between a batch and its refetch.
let moveChain = Promise.resolve();

function enqueueWrite(run) {
  moveChain = moveChain.then(run).catch(() => {}); // never leave the chain rejected
  return moveChain;
}

function moveTasksToSprint(keys, priority) {
  if (!keys.length || !Number.isInteger(priority)) return moveChain;
  const projectId = state.projectId;

  // Optimistic: rows leave the unscheduled list at once — and renderBacklog() drops them from
  // the selection with them, so the bulk count never counts a row no longer rendered.
  keys.forEach(k => {
    const t = state.tasks.find(t => t.task_key === k);
    if (t) { bumpPriorityCount(t.priority, priority); t.priority = priority; }
  });
  renderBacklog();

  return enqueueWrite(async () => {
    if (state.projectId !== projectId) return; // navigated away mid-batch

    const failed = [];
    let cascaded = false;
    let landed = priority;

    for (const key of keys) {
      try {
        const res = await api.patch(`/projects/${projectId}/tasks/${key}`, { priority });
        if (res && res.task) {
          // Reconcile: the server's post-cascade priority wins over the optimistic value.
          const idx = state.tasks.findIndex(t => t.task_key === key);
          if (idx >= 0) {
            if (typeof res.task.priority === 'number') bumpPriorityCount(state.tasks[idx].priority, res.task.priority);
            state.tasks[idx] = { ...state.tasks[idx], ...res.task };
          }
          if (keys.length === 1 && typeof res.task.priority === 'number') landed = res.task.priority;
        }
        if (res && (res.children_rescheduled || res.parent_rescheduled)) cascaded = true;
      } catch (err) {
        failed.push(key); // a bad key must not strand the rest of the batch
      }
    }

    if (state.projectId !== projectId) return;

    if (failed.length) {
      showToast(`Failed to move ${failed.length} task${failed.length !== 1 ? 's' : ''}`, 'error');
      await refetchTasks(); // server is the truth for the whole batch now
      return;
    }
    if (cascaded) {
      // Tasks OTHER than these moved server-side (C1464 children / TPT2 ancestors) — refetch
      // instead of trusting the per-key reconciliation, same discipline as sprint-board.js and
      // sprint-planning.js (see tt-web-sprint.md § Objective sprint cascade refetch).
      await refetchTasks();
    } else {
      renderBacklog(); // reconciled priorities feed the option lists on the next render
    }
    const label = sprintSingular();
    showToast(keys.length === 1
      ? `Moved ${keys[0]} to ${label} ${landed}`
      : `Moved ${keys.length} tasks to ${label} ${priority}`, 'success');
  });
}

// (TPT43) Within-list reorder persistence — display_order only, never a `priority` field.
// Routed through the same enqueueWrite() chain moveTasksToSprint() uses (see its header
// comment) so a reorder can never interleave with an in-flight sprint-move batch's refetch.
function persistBacklogOrder(patches) {
  const projectId = state.projectId;
  return enqueueWrite(async () => {
    if (state.projectId !== projectId) return; // navigated away mid-drag
    try {
      await Promise.all(patches.map(({ taskKey, patch }) =>
        api.patch(`/projects/${projectId}/tasks/${taskKey}`, patch)));
    } catch (err) {
      if (state.projectId !== projectId) return;
      showToast('Failed to save order', 'error');
      await refetchTasks();
    }
  });
}

// ── Inline create ──

async function handleInlineCreate() {
  const titleInput = document.getElementById('backlog-new-title');
  const typeSelect = document.getElementById('backlog-new-type');
  if (!titleInput) return;

  const title = titleInput.value.trim();
  const type = typeSelect ? typeSelect.value : 'task';

  if (!title) {
    showToast('Title required', 'error');
    return;
  }
  if (type !== 'epic' && (!inlineReservedTask || inlineReservedTask.type !== type)) {
    // Reservation still in flight, or hasn't caught up with a just-changed type yet.
    showToast('Still reserving a task key — try again in a moment', 'error');
    return;
  }

  try {
    let task;
    if (type === 'epic') {
      // No reservation for epics — the server auto-generates the key (generateEpicKey).
      ({ task } = await api.post(`/projects/${state.projectId}/tasks`, {
        title, type, priority: 0, category: 'CODING', description: '',
      }));
    } else {
      // Finalize the already-reserved placeholder row in place — never a second create.
      // Explicit '' clears the reservation placeholder's sentinel description.
      const taskKey = inlineReservedTask.task_key;
      ({ task } = await api.patch(`/projects/${state.projectId}/tasks/${taskKey}`, {
        title, priority: 0, category: 'CODING', description: '',
        is_objective: true, // top-level web create = objective (C1341); epic branch above excluded
      }));
      // Consumed, not abandoned — clear without releasing (it's now real content).
      inlineReservedTask = null;
    }
    const idx = state.tasks.findIndex(t => t.task_key === task.task_key);
    if (idx >= 0) state.tasks[idx] = task;
    else { state.tasks.push(task); bumpPriorityCount(null, task.priority); }
    renderBacklog();
    showToast('Task created', 'success');
  } catch (err) {
    showToast(err.message || 'Failed to create task', 'error');
  }
}

// ── Create Epic modal ──

function openCreateEpicModal() {
  const body = `
    <form id="create-epic-form" style="display:flex;flex-direction:column;gap:0.75rem;">
      <div>
        <label class="form-label">Title</label>
        <input class="form-input" name="title" placeholder="Epic title" required>
      </div>
      <div>
        <label class="form-label">Description</label>
        <textarea class="form-textarea" name="description" rows="3" placeholder="Describe this epic..."></textarea>
      </div>
      <button type="submit" class="btn btn-primary">Create Epic</button>
    </form>`;

  state.modalToken = openModal('Create Epic', body, {
    onMount(overlay) {
      overlay.querySelector('#create-epic-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target;
        const data = {
          title: form.title.value.trim(),
          description: form.description.value.trim(),
          type: 'epic',
          category: 'CODING',
          status: roleStatus(state.statuses, 'start')?.name || 'pending',
        };
        if (!data.title) return;

        try {
          const res = await api.post('/projects/' + state.projectId + '/tasks', data);
          state.tasks.push(res.task);
          bumpPriorityCount(null, res.task.priority);
          closeModal();
          renderBacklog();
          showToast('Epic created', 'success');
        } catch (err) {
          showToast(err.message || 'Failed to create epic', 'error');
        }
      });
    },
  });
}

// ── Data (TPT630) ──
//
// The backlog rows and the facets — never an unfiltered GET /tasks. The facets leg fails open
// to null; callers keep whatever sprint numbers/counts they already hold.
function loadBacklogData(projectId) {
  return Promise.all([
    api.get('/projects/' + projectId + '/tasks?priority=0&fields=board'),
    api.get('/projects/' + projectId + '/tasks/facets').catch(() => null),
  ]);
}

function applyFacets(facets) {
  if (!facets) return;
  state.sprintRows = (facets.priorities || []).map(priority => ({ priority }));
  state.priorityCounts = { ...(facets.priority_counts || {}) };
}

// ── Refetch helper ──

async function refetchTasks() {
  const owner = state;
  try {
    const [{ tasks }, facets] = await loadBacklogData(owner.projectId);
    if (state !== owner) return; // navigated away mid-fetch
    state.tasks.splice(0, state.tasks.length, ...(tasks || []));
    applyFacets(facets);
  } catch (_) {}
  if (state !== owner) return;
  renderBacklog();
}

// ── Task detail ──

// A backlog row is usually a top-level objective whose children sit in sprints, outside this
// page's rows — the drawer lists children from options.allTasks, so they are read first (one
// parent_id-scoped request). The backlog row objects themselves are passed through unchanged,
// so the drawer's in-place sync-back still updates them.
async function openTaskDetailPanel(taskKey) {
  const owner = state;
  const row = state.tasks.find(t => t.task_key === taskKey);
  let children = [];
  if (row && row.id != null) {
    const res = await api.get(`/projects/${owner.projectId}/tasks?parent_id=${encodeURIComponent(row.id)}&fields=board`)
      .catch(() => null);
    if (state !== owner) return; // navigated away mid-fetch
    const known = new Set(state.tasks.map(t => t.task_key));
    children = ((res && res.tasks) || []).filter(t => !known.has(t.task_key));
  }
  owner.detailToken = openTaskDetail(owner.projectId, taskKey, {
    allTasks: [...state.tasks, ...children],
    sprintRows: sprintSourceRows(),
    onClose: async () => {
      await refetchTasks();
    },
  });
}

// ── Assignee (single row, TPT44) ──

// Serialized through the same chain as the bulk path and every other write on this page (see
// moveTasksToSprint()'s header comment) — a single-row assignee PATCH must not interleave
// with an in-flight sprint-move batch either.
function handleAssign(taskKey, userId) {
  const task = state.tasks.find(t => t.task_key === taskKey);
  if (!task) return moveChain;
  const old = task.assignee;
  const projectId = state.projectId;
  task.assignee = userId;
  renderBacklog();

  return enqueueWrite(async () => {
    if (state.projectId !== projectId) return; // navigated away mid-write
    try {
      await api.patch(`/projects/${projectId}/tasks/${taskKey}`, { assignee: userId });
    } catch (err) {
      if (state.projectId !== projectId) return;
      task.assignee = old;
      renderBacklog();
      showToast('Failed to assign', 'error');
    }
  });
}

// Row combo (TPT44) — avatar at rest, expands into a member-combo on click. Only ONE
// instance is ever live; renderBacklog() also closes it unconditionally on every paint (see
// its header comment) since an orphaned instance leaks a window-resize listener.
function closeRowCombo() {
  if (!rowCombo) return;
  const { taskKey, wrap, instance } = rowCombo;
  rowCombo = null;
  instance.destroy();
  if (!document.body.contains(wrap)) return; // renderBacklog() already discarded this node
  wrap.classList.remove('backlog-row-assignee-open');
  const task = state.tasks.find(t => t.task_key === taskKey);
  const member = task && task.assignee != null
    ? state.members.find(m => (m.user_id || m.id) === task.assignee)
    : null;
  wrap.innerHTML = `<span class="backlog-row-avatar">${renderAvatarHtml(member, 24)}</span>`;
}

function openRowCombo(wrap, taskKey) {
  closeRowCombo(); // at most one live instance
  wrap.classList.add('backlog-row-assignee-open');
  const task = state.tasks.find(t => t.task_key === taskKey);
  wrap.innerHTML = renderMemberCombo({
    id: `backlog-assignee-${taskKey}`,
    assignee: task ? task.assignee : null,
    members: state.members,
  });

  const instance = createMemberCombo(wrap, {
    getMembers: () => state.members, // live — never a captured snapshot
    getValue: () => {
      const t = state.tasks.find(t => t.task_key === taskKey);
      return t ? t.assignee : null;
    },
    setValue: () => {}, // handleAssign() below owns the real write + optimistic state
    onCommit: (userId) => {
      // Close FIRST — handleAssign() calls renderBacklog(), which wipes this wrap; destroying
      // the instance after that would try to operate on already-detached DOM.
      closeRowCombo();
      handleAssign(taskKey, userId);
    },
    isAlive: () => document.body.contains(wrap),
    scrollEl: window, // .main is not itself a scroll container — the window is
  });
  rowCombo = { taskKey, wrap, instance };

  const input = wrap.querySelector('.member-combo-input');
  if (input) {
    input.focus(); // combo's own focus handler does select() + opens the dropdown
    // A real blur (not the dropdown's mousedown+preventDefault commit path) means the user
    // clicked away — collapse back to the avatar. Escape with the dropdown already closed
    // calls input.blur() inside the combo, so Escape collapses through this same path too.
    input.addEventListener('blur', () => {
      setTimeout(() => {
        if (rowCombo && rowCombo.taskKey === taskKey) closeRowCombo();
      }, 200);
    });
  }
}

// ── Drag and drop (TPT43) ──
//
// Reinstates the pre-C1395 drag on this page (removed along with per-sprint lanes; this
// does NOT bring lanes back, only a slim rail of drop targets), rebuilt on the shared
// lib/drag-order.js helpers. House idiom throughout: clone + placeholder + a `-hidden`
// class toggled on the dragged original (stays in the DOM, only hidden), 5px move
// threshold, listeners on `document` — same shape as sprint-planning.js/sprint-board.js.
//
// Drag SOURCE is the whole `.backlog-row`, not a dedicated grip handle (deliberately unlike
// sprint-planning.js and unlike this page's own pre-C1395 code) — guarded by
// ROW_INTERACTIVE_SELECTOR, the same exclusion list the row-click→detail guard uses below,
// so the two can never drift apart.
//
// TWO drop zones:
//  - `.backlog-list` — ORDERED. Uses findInsertBefore()/dropIndex() to persist display_order
//    only; every row here is already priority 0, so a reorder patch never carries `priority`.
//  - `.backlog-sprint-rail-target` — UNORDERED. Uses neither helper (same category as
//    kanban.js's card-into-column drag); resolves the sprint number off the target's own
//    data-priority and routes through moveTasksToSprint() — NEVER a direct PATCH, so the
//    serialized moveChain / cascade reconciliation / toast are never bypassed.

const DRAG_THRESHOLD = 5;
let onMouseDown, onMouseMove, onMouseUp;
// A moved drag that ends on its own source row still emits a native `click` right after
// mouseup — swallowed here so it can't re-open the just-dragged row's detail panel. Reset
// at the TOP of every mousedown, so it can never survive to eat an unrelated later click.
let suppressRowClick = false;

// The row's interactive children — excluded from BOTH the row-click→detail guard
// (bindBacklogEvents, below) and the drag source (onMouseDown, below).
const ROW_INTERACTIVE_SELECTOR = '.backlog-checkbox, .card-avatar-wrap, .member-combo, select, button, input, a';

function clearDropHighlights() {
  document.querySelectorAll('.backlog-list.drag-over, .backlog-sprint-rail-target.drag-over')
    .forEach(el => el.classList.remove('drag-over'));
}

// Tears down an in-flight drag's DOM without unbinding the document listeners — safe to
// call from renderBacklog() (an innerHTML wipe mid-drag must not leave onMouseMove/onMouseUp
// operating on detached nodes).
function abortDrag() {
  const d = state.drag;
  if (d) {
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    if (d.el) d.el.classList.remove('backlog-row-hidden');
    state.drag = null;
  }
  clearDropHighlights();
  document.getElementById('backlog-sprint-rail')?.classList.remove('backlog-rail-drag-open');
}

function bindDragAndDrop() {
  onMouseDown = (e) => {
    suppressRowClick = false;
    if (e.button !== 0) return; // left button only
    if (!canWrite()) return; // reorder/move needs task:write — the row click still opens the task
    const row = e.target.closest('.backlog-row');
    if (!row || e.target.closest(ROW_INTERACTIVE_SELECTOR)) return;

    const rect = row.getBoundingClientRect();
    state.drag = {
      taskKey: row.dataset.taskKey,
      el: row, clone: null, placeholder: null,
      startX: e.clientX, startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    // Cancels only text-selection/focus on mousedown — never `click` or anchor navigation.
    // Everything with a mousedown side effect worth preserving (checkbox, select, anchor,
    // avatar wrap) is already excluded above by ROW_INTERACTIVE_SELECTOR.
    e.preventDefault();
  };

  onMouseMove = (e) => {
    const d = state.drag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('backlog-row-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      document.body.appendChild(d.clone);

      d.placeholder = document.createElement('div');
      d.placeholder.className = 'backlog-row-placeholder';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      d.el.classList.add('backlog-row-hidden');
      // A collapsed rail has no visible targets — force it open for the drag's duration.
      // Free: the rail's grid track is a constant width either way (see styles.css).
      document.getElementById('backlog-sprint-rail')?.classList.add('backlog-rail-drag-open');
    }

    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    clearDropHighlights();
    const point = document.elementFromPoint(e.clientX, e.clientY);
    if (!point) return;

    // ── Branch A: rail target — unordered drop zone, no placeholder move, no hit-test. ──
    const railTarget = point.closest('.backlog-sprint-rail-target');
    if (railTarget) { railTarget.classList.add('drag-over'); return; }

    // ── Branch B: within-list reorder — ordered drop zone. ──
    const list = point.closest('.backlog-list');
    if (!list) return;
    list.classList.add('drag-over');
    const rows = [...list.querySelectorAll('.backlog-row:not(.backlog-row-hidden)')];
    const insertBefore = findInsertBefore(rows, e.clientX, e.clientY, 'y');
    if (insertBefore) list.insertBefore(d.placeholder, insertBefore);
    else list.appendChild(d.placeholder);
  };

  onMouseUp = async (e) => {
    const d = state.drag;
    if (!d) return;
    clearDropHighlights();

    // Plain click (never crossed the threshold) — do nothing here. The container's own
    // delegated click handler still fires and opens the detail panel, exactly as before
    // this change.
    if (!d.moved) { state.drag = null; return; }

    suppressRowClick = true;
    document.getElementById('backlog-sprint-rail')?.classList.remove('backlog-rail-drag-open');

    // Resolve the drop target from the POINTER, not from a node remembered during
    // mousemove — an async refetch could rebuild the rail mid-drag.
    const point = document.elementFromPoint(e.clientX, e.clientY);
    const railTarget = point ? point.closest('.backlog-sprint-rail-target') : null;
    const list = railTarget ? null : d.placeholder?.closest('.backlog-list');

    // MUST run before the cleanup below un-hides d.el — dropIndex() counts elements NOT
    // carrying the hidden class (see lib/drag-order.js).
    const placeholderIndex = list
      ? dropIndex(list, d.placeholder, { selector: '.backlog-row', hiddenClass: 'backlog-row-hidden' })
      : 0;

    // Cleanup DOM before any re-render: moveTasksToSprint()/renderBacklog() wipe
    // container.innerHTML synchronously in their optimistic step.
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('backlog-row-hidden');

    const taskKey = d.taskKey;
    const task = state.tasks.find(t => t.task_key === taskKey);
    state.drag = null;
    if (!task) return;

    // ── Branch A: rail drop → priority move ──
    if (railTarget) {
      const priority = parseInt(railTarget.dataset.priority, 10);
      if (!Number.isFinite(priority) || priority <= 0) return;
      // THE funnel — never PATCH priority directly. moveTasksToSprint() owns the serialized
      // moveChain, the optimistic write + selection clear, post-cascade reconciliation
      // (server priority can come back HIGHER), the cascade refetch and the toast.
      moveTasksToSprint([taskKey], priority);
      return;
    }

    // ── Branch B: within-list reorder → display_order only ──
    if (!list) return;
    // Every row in .backlog-list is already priority 0 by construction (renderBacklog
    // filters on it), so — unlike sprint-planning.js/sprint-board.js's shared template —
    // there is no priorityChanged term and no patch here ever carries a `priority` field.
    const laneTasks = state.tasks
      .filter(t => t.priority === 0 && t.task_key !== taskKey)
      .sort((a, b) => b.display_order - a.display_order);
    const insertAt = Math.min(placeholderIndex, laneTasks.length);
    laneTasks.splice(insertAt, 0, task);

    const patches = [];
    laneTasks.forEach((t, i) => {
      const newOrder = (laneTasks.length - i) * 10;
      if (t.display_order !== newOrder) patches.push({ taskKey: t.task_key, patch: { display_order: newOrder } });
    });
    if (patches.length === 0) return;

    laneTasks.forEach((t, i) => { t.display_order = (laneTasks.length - i) * 10; });
    renderBacklog();
    await persistBacklogOrder(patches);
  };

  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

function cleanupDrag() {
  abortDrag();
  if (onMouseDown) document.removeEventListener('mousedown', onMouseDown);
  if (onMouseMove) document.removeEventListener('mousemove', onMouseMove);
  if (onMouseUp) document.removeEventListener('mouseup', onMouseUp);
}

// ── Events (delegation) ──

function bindBacklogEvents() {
  const container = document.getElementById('backlog-container');
  if (!container) return;

  container.addEventListener('click', (e) => {
    // A moved drag that ended on its own source row still emits this click — swallow it
    // once so it can't re-open the just-dragged row's detail panel (TPT43).
    const wasDrag = suppressRowClick;
    suppressRowClick = false;

    // Avatar → row assignee combo (TPT44). Clicks landing on the combo itself (once open)
    // re-enter this same delegate — the open-check guards against tearing it down mid-use.
    const avatarWrap = e.target.closest('.card-avatar-wrap');
    if (avatarWrap) {
      e.preventDefault();
      if (!avatarWrap.classList.contains('backlog-row-assignee-open')) {
        openRowCombo(avatarWrap, avatarWrap.dataset.taskKey);
      }
      return;
    }

    // Sprint rail collapse toggle (TPT43). Flips the class IN PLACE — never renderBacklog(),
    // which would re-enter releaseInlineReservation() and drop a key the user is mid-typing.
    const railToggle = e.target.closest('.backlog-rail-toggle');
    if (railToggle) {
      railCollapsed = !railCollapsed;
      const rail = document.getElementById('backlog-sprint-rail');
      if (rail) rail.classList.toggle('backlog-sprint-rail-collapsed', railCollapsed);
      railToggle.setAttribute('aria-expanded', String(!railCollapsed));
      railToggle.title = railCollapsed ? 'Expand' : 'Collapse';
      return;
    }

    // Row click → task detail (exclude interactive els)
    const row = e.target.closest('.backlog-row');
    if (row && !wasDrag && !e.target.closest(ROW_INTERACTIVE_SELECTOR)) {
      openTaskDetailPanel(row.dataset.taskKey);
      return;
    }
  });

  // Checkbox changes — the selection's listener (syncSelectionUi) and the bulk toolbar
  // follow from the selection itself.
  container.addEventListener('change', (e) => {
    const cb = e.target.closest('.backlog-checkbox');
    if (cb) {
      state.selection.toggle(cb.dataset.taskKey, cb.checked);
      return;
    }
    if (e.target.closest('.backlog-select-all')) {
      const keys = [...container.querySelectorAll('.backlog-checkbox')].map(box => box.dataset.taskKey);
      if (e.target.checked) state.selection.addAll(keys);
      else state.selection.deleteAll(keys);
      return;
    }

    // Per-row move. Reset the control BEFORE dispatching: the handler re-renders this row
    // away synchronously (renderBacklog() inside moveTasksToSprint()'s optimistic step), so a
    // reset scheduled after would run on a now-detached node.
    const rowSprint = e.target.closest('.backlog-row-sprint');
    if (rowSprint) {
      const val = parseInt(rowSprint.value, 10);
      rowSprint.selectedIndex = 0;
      if (!isNaN(val)) moveTasksToSprint([rowSprint.dataset.taskKey], val);
    }
  });

  // Inline create (Enter on key or title input)
  container.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.target.id === 'backlog-new-key' || e.target.id === 'backlog-new-title')) {
      e.preventDefault();
      handleInlineCreate();
    }
  });

  // Book (or re-book, on type change) a real key the moment the user starts
  // interacting with the inline-create row — not on every render.
  container.addEventListener('focusin', (e) => {
    if (e.target.id === 'backlog-new-key' || e.target.id === 'backlog-new-title') {
      syncInlineReservation();
    }
  });
  container.addEventListener('change', (e) => {
    if (e.target.id === 'backlog-new-type') {
      syncInlineReservation();
    }
  });
  // Release if focus leaves the whole inline-create row without submitting —
  // moving focus between the row's own inputs must NOT release.
  container.addEventListener('focusout', (e) => {
    const row = document.getElementById('backlog-inline-create');
    if (!row || !row.contains(e.target)) return;
    if (e.relatedTarget && row.contains(e.relatedTarget)) return;
    releaseInlineReservation();
  });
}

// ── Page entry ──

export async function backlogPage(params) {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;
  resetState();
  const owner = state;
  state.projectId = params.id;
  // Module-scoped so a re-mount of the SAME project keeps the user's collapse choice
  // (resetState() runs every mount) — reset only on a genuine project switch, per
  // sprint-board.js's collapsedLanes/collapsedLanesProjectId idiom.
  if (railCollapsedProjectId !== params.id) {
    railCollapsed = false;
    railCollapsedProjectId = params.id;
  }

  initLayout(getUser());
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'backlog', viewLabel: 'Backlog' });

  const content = getContentEl();
  // #backlog-bulk-host sits between the header and the list, a direct child of the page so its
  // sticky toolbar travels the whole page, and outside #backlog-container so no
  // renderBacklog() ever rebuilds it (TPT9098).
  content.innerHTML = `
    <div class="page-header access-pending" id="backlog-header">
      <h2>Backlog</h2>
      <button class="btn btn-primary needs-write" id="create-epic-btn">+ Create Epic</button>
    </div>
    <div id="backlog-bulk-host"></div>
    <div id="backlog-container"><div class="empty"><span class="spinner"></span></div></div>`;
  content.querySelector('#create-epic-btn').addEventListener('click', openCreateEpicModal);

  try {
    const [[tasksRes, facets], membersRes, statuses, sprintsRes, project] = await Promise.all([
      loadBacklogData(params.id),
      api.get('/projects/' + params.id + '/members'),
      loadStatuses(params.id),
      api.get('/projects/' + params.id + '/sprints').catch(() => ({ sprints: [] })),
      // C1334/C1395 — sprints_enabled gates both sprint selects, task_group_label names them.
      // Shared per-project cache, fails open to null (read as enabled/default noun) on error —
      // same discipline as board.js / sprint-planning.js / task-detail.js.
      loadProject(params.id),
    ]);
    if (superseded()) return;
    state.tasks = tasksRes.tasks || [];
    applyFacets(facets);
    state.members = membersRes.members || [];
    state.statuses = statuses || [];
    state.sprints = sprintsRes.sprints || [];
    state.project = project || null;
    applyWriteGate(document.getElementById('backlog-header'), canWrite());
    state.selection.subscribe(syncSelectionUi);
    if (bulkToolbar) { bulkToolbar.destroy(); bulkToolbar = null; } // a mount whose teardown has not run yet
    if (canWrite()) {
      bulkToolbar = renderBulkToolbar(document.getElementById('backlog-bulk-host'), {
        selection: state.selection,
        projectId: params.id,
        getProject: () => state.project,
        getMembers: () => state.members,
        getSprints: () => state.sprints,
        getSprintRows: sprintSourceRows,
        getTask: key => state.tasks.find(t => t.task_key === key),
        includeBacklog: false, // every row here already is backlog
        enqueue: enqueueWrite,
        onApplied: applyBulkResult,
        onFailed: () => refetchTasks(), // the server is the truth after a failed batch
      });
    }
    renderBacklog();
    bindBacklogEvents();
    bindDragAndDrop();
  } catch (err) {
    if (superseded()) return;
    applyWriteGate(document.getElementById('backlog-header'), false);
    document.getElementById('backlog-container').innerHTML =
      `<div class="empty text-muted">Failed to load tasks</div>`;
  }

  return () => {
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer mount of
    // this page alone: that mount owns the module state by then.
    if (state !== owner) return;
    cleanupDrag();
    releaseInlineReservation();
    closeRowCombo();
    if (bulkToolbar) { bulkToolbar.destroy(); bulkToolbar = null; }
    closeTaskDetail(state.detailToken);
    closeModal(state.modalToken);
    resetState();
  };
}
